import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { api } from '@/api';
import type { WrappedItemsResult, WrappedResult } from '@/api/types';
import { DrawerForm } from '@/components/drawer-form';
import { formulaStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

import { getFormFields } from './setup';

const entity = 'formulas';
const store = formulaStore;

export const ContractTransfer = observer(({ transition }: UIViewInjectedProps) => {
  const { formulaId: itemId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +itemId);
  const foundItemContractsIds = useMemo(() => foundItem?.contracts.map(({ id }) => id) || [], [foundItem]);
  const contractProductId = +foundItem?.product.id;

  const [contracts, setContracts] = useState<cyrian.api.formulas.Contract[]>([]);
  const [targetContracts, setTargetContracts] = useState<number[]>(foundItemContractsIds);
  const [loading, setLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<boolean>(false);

  useEffect(() => {
    if (!foundItem) {
      return;
    }
    setLoading(true);
    api.formulas
      .getAvailableContracts(itemId)
      .source.then(({ data }: WrappedItemsResult<cyrian.api.formulas.Contract[]>) => {
        setTargetContracts(foundItemContractsIds);
        setContracts([...data.items, ...(foundItem?.contracts || [])]);
      })
      .then(() => setLoading(false));
  }, [foundItem, foundItemContractsIds, itemId]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const title = getTranslatedString('formulas.formula-number', { 0: itemId });
  const formFields = getFormFields(
    contractProductId,
    contracts,
    targetContracts,
    warning,
    setTargetContracts,
    setWarning,
  );

  const onSubmit = (values: any) => {
    const { contracts: finalContracts } = values;
    const addDiff = finalContracts.filter((contractId) => !foundItemContractsIds.includes(contractId));
    const removeDiff = foundItemContractsIds.filter((contractId) => !finalContracts.includes(contractId));
    const promises = [];

    if (addDiff.length) {
      promises.push(
        api.formulas
          .addContracts(itemId, { contractIds: addDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.formulas.Formula>) => store.updateItem(data))
          .catch(showError),
      );
    }

    if (removeDiff.length) {
      promises.push(
        api.formulas
          .removeContracts(itemId, { contractIds: removeDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.formulas.Formula>) => store.updateItem(data))
          .catch(showError),
      );
    }

    return Promise.all(promises);
  };

  const resourceController = {
    create: onSubmit,
  };

  const layout = {
    labelCol: { span: 0 },
    wrapperCol: { span: 24 },
    tailLayout: { wrapperCol: { offset: 0, span: 24 } },
  };

  return (
    <DrawerForm
      {...layout}
      title={title}
      initValues={{}}
      formFields={formFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || !foundItem}
    />
  );
});
